import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, getCSRFTokenFromServer, setCSRFToken, getCSRFToken, checkAuthStatus } from './apiUtils';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Input } from './components/ui/input';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Search, Save, CheckCircle, AlertCircle, Calendar, Wine, Coffee } from 'lucide-react';
import useBusinessDate from './hooks/useBusinessDate';
import { formatDateDash } from './utils/formatters';
import { toast } from 'sonner';
import { useNavigation } from './contexts/NavigationContext';

function UpdateClosingStock() {
 const { logout } = useNavigation();
 const businessDate = useBusinessDate();
 const [activeTab, setActiveTab] = useState('liquor');
 
 // Liquor state
 const [stockData, setStockData] = useState([]);
 const [filteredData, setFilteredData] = useState([]);
 const [loading, setLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [saving, setSaving] = useState(false);
 const [saveProgress, setSaveProgress] = useState(0);
 const [saveStatus, setSaveStatus] = useState('');
 const [editingValues, setEditingValues] = useState({});
 const [closingStockStatus, setClosingStockStatus] = useState(null);
 const [originalValues, setOriginalValues] = useState({});
 const [retryCount, setRetryCount] = useState(0);
 
 // Accessories state
 const [accessoryData, setAccessoryData] = useState([]);
 const [filteredAccessories, setFilteredAccessories] = useState([]);
 const [accessorySearchTerm, setAccessorySearchTerm] = useState('');
 const [accessoryLoading, setAccessoryLoading] = useState(false);
 const [accessorySaving, setAccessorySaving] = useState(false);
 const [accessoryEditingValues, setAccessoryEditingValues] = useState({});
const [accessoryReceivedEditingValues, setAccessoryReceivedEditingValues] = useState({});
 const [accessoryOriginalValues, setAccessoryOriginalValues] = useState({});
const [accessoryOriginalReceivedValues, setAccessoryOriginalReceivedValues] = useState({});
 const [accessoryStatus, setAccessoryStatus] = useState(null);

// Group inventory items by brand name for rowspan display and add pack type indicators
const getGroupedInventoryForDisplay = () => {
  const groups = {};
  const result = [];
  
  // First, identify products that have multiple pack types for the same size code
  const sizeCodePackTypes = {};
  filteredData.forEach(item => {
    const key = `${item.brandName}_${item.size}`;
    if (!sizeCodePackTypes[key]) {
      sizeCodePackTypes[key] = new Set();
    }
    sizeCodePackTypes[key].add(item.packType);
  });
  
  // Group items by brand name
  filteredData.forEach((item, originalIndex) => {
    const brandName = item.brandName;
    if (!groups[brandName]) {
      groups[brandName] = [];
    }
    
    // Determine if we need to show pack type indicator
    const sizeKey = `${item.brandName}_${item.size}`;
    const hasMultiplePackTypes = sizeCodePackTypes[sizeKey].size > 1;
    const displaySize = hasMultiplePackTypes 
      ? `${item.size} (${item.packType})`
      : item.size;
    
    groups[brandName].push({
      ...item,
      displaySize,
      originalIndex
    });
  });
  
  // Convert to display format with rowspan info
  let serialNumber = 1;
  Object.keys(groups).forEach(brandName => {
    const variants = groups[brandName];
    
    // Sort variants by size in descending order (QQ > PP > NN)
    // Size format is like "QQ(750ml)", extract the number and sort
    variants.sort((a, b) => {
      const sizeA = parseInt(a.size.match(/\((\d+)ml\)/)?.[1] || 0);
      const sizeB = parseInt(b.size.match(/\((\d+)ml\)/)?.[1] || 0);
      return sizeB - sizeA; // Descending order
    });
    
    variants.forEach((variant, variantIndex) => {
      result.push({
        ...variant,
        brandName,
        isFirstVariant: variantIndex === 0,
        isLastVariant: variantIndex === variants.length - 1,
        variantCount: variants.length,
        serialNumber: variantIndex === 0 ? serialNumber : null
      });
    });
    serialNumber++;
  });
  
  return result;
};


 useEffect(() => {
   fetchTodayStock();
  fetchAccessoryStock();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
   if (searchTerm) {
     const filtered = stockData.filter(item => 
       item.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
       item.brandNumber.includes(searchTerm)
     );
     setFilteredData(filtered);
   } else {
     setFilteredData(stockData);
   }
 }, [searchTerm, stockData]);

 // Filter accessories based on search
 useEffect(() => {
   if (accessorySearchTerm) {
     const filtered = accessoryData.filter(item => 
       item.name.toLowerCase().includes(accessorySearchTerm.toLowerCase())
     );
     setFilteredAccessories(filtered);
   } else {
     setFilteredAccessories(accessoryData);
   }
 }, [accessorySearchTerm, accessoryData]);

 const fetchAccessoryStock = async () => {
   setAccessoryLoading(true);
   try {
     const response = await apiGet(`/api/accessories/daily?date=${businessDate || ''}`);
     const data = await response.json();
     
     if (data.accessories) {
       const processedData = data.accessories.map((item, index) => {
         const openingStock = item.opening_stock || 0;
         const receivedStock = item.received_stock || 0;
         const totalStock = openingStock + receivedStock;
         const closingStock = item.closing_stock !== null ? item.closing_stock : totalStock;
         const isClosingStockSet = item.closing_stock !== null;
         
         return {
           id: item.accessory_id,
           recordId: item.record_id,
           serialNo: index + 1,
           name: item.name,
           size: item.size || '-',
           openingStock,
           received: receivedStock,
           total: totalStock,
           closingStock,
           isClosingStockSet,
           sales: Math.max(0, totalStock - closingStock),
           pricePerUnit: parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0
         };
       });
       
       setAccessoryData(processedData);
       setFilteredAccessories(processedData);
       
       // Store original values
       const originalClosingStocks = {};
       const originalReceivedStocks = {};
       processedData.forEach(item => {
         originalClosingStocks[item.id] = item.closingStock;
         originalReceivedStocks[item.id] = item.received;
       });
       setAccessoryOriginalValues(originalClosingStocks);
       setAccessoryOriginalReceivedValues(originalReceivedStocks);
       
       // Set status
       const savedCount = processedData.filter(a => a.isClosingStockSet).length;
       setAccessoryStatus({
         totalProducts: processedData.length,
         savedProducts: savedCount,
         isFullySaved: savedCount === processedData.length && processedData.length > 0
       });
     }
   } catch (error) {
     console.error('Error fetching accessory stock:', error);
   }
   setAccessoryLoading(false);
 };

 const fetchTodayStock = async () => {
   try {
    const response = await apiGet('/api/shop/products');
    const responseData = await response.json();
      
      // Handle both old and new API response formats
      const products = responseData.products || responseData;
      const closingStatus = responseData.closingStockStatus || null;
      
      const processedData = products
        .map((item, index) => {
          const openingStock = item.openingStock || 0;
          const receivedStock = item.receivedStock || 0;
          const totalStock = openingStock + receivedStock;
          // If closing_stock is NULL, display total stock but keep track that it's not set
          const closingStock = item.closingStock !== null ? item.closingStock : totalStock;
          const isClosingStockSet = item.closingStock !== null;
          
          return {
            id: item.id,
            serialNo: index + 1,
            brandName: item.name,
            brandNumber: item.brandNumber,
            size: `${item.sizeCode}(${item.size}ml)`,
            packType: item.packType,
            openingStock: openingStock,
            received: receivedStock,
            total: totalStock,
            closingStock: closingStock,
            isClosingStockSet: isClosingStockSet,
            sales: Math.max(0, totalStock - closingStock),
            pricePerUnit: parseFloat(item.finalPrice) || 0
          };
        })
        // Filter out products where O.S, Received, and Total are all zero
        .filter(item => item.openingStock !== 0 || item.received !== 0 || item.total !== 0);

      setStockData(processedData);
      setFilteredData(processedData);
      setClosingStockStatus(closingStatus);
      
      // Store original closing stock values for change tracking
      const originalClosingStocks = {};
      processedData.forEach(item => {
        originalClosingStocks[item.id] = item.closingStock;
      });
      setOriginalValues(originalClosingStocks);
 } catch (error) {
   console.error('Error fetching stock data:', error);
 }
   setLoading(false);
 };

 const handleClosingStockChange = (id, value) => {
  // Find the item to get its total (TTL) value
  const currentItem = stockData.find(item => item.id === id);
  if (!currentItem) return;
  
  // Store the raw input value in editingValues for display purposes
  // This allows showing what the user is actually typing
  setEditingValues(prev => ({ ...prev, [id]: value }));
  
  // Parse the value for validation and state update
  let numValue;
  if (value === '' || value === null || value === undefined) {
    numValue = 0;
  } else {
    numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) {
      numValue = 0;
    }
  }
  
  // Cap at total stock
  const validatedValue = Math.min(numValue, currentItem.total);
  
  // Update with numeric value
  const updateData = (data) => data.map(item => {
    if (item.id === id) {
      return {
        ...item,
        closingStock: validatedValue,
        sales: Math.max(0, item.total - validatedValue),
        isClosingStockSet: true
      };
    }
    return item;
  });
  
  setFilteredData(updateData(filteredData));
  setStockData(updateData(stockData));
};

const handleClosingStockBlur = (id) => {
  // Clear editing value - field will now show the actual value
  setEditingValues(prev => {
    const newValues = { ...prev };
    delete newValues[id];
    return newValues;
  });
};

const handleClosingStockFocus = () => {
  // Focus handler - currently no-op
};

 // Helper function to check if there are unsaved changes
 const hasUnsavedChanges = () => {
   return stockData.some(item => {
     const currentValue = item.closingStock;
     const originalValue = originalValues[item.id];
     return currentValue !== originalValue;
   });
 };

 // Accessory handlers
 const handleAccessoryClosingStockChange = (id, value) => {
   const currentItem = accessoryData.find(item => item.id === id);
   if (!currentItem) return;
   
   setAccessoryEditingValues(prev => ({ ...prev, [id]: value }));
   
   let numValue;
   if (value === '' || value === null || value === undefined) {
     numValue = 0;
   } else {
     numValue = parseInt(value);
     if (isNaN(numValue) || numValue < 0) numValue = 0;
   }
   
   const validatedValue = Math.min(numValue, currentItem.total);
   
   const updateData = (data) => data.map(item => {
     if (item.id === id) {
       return {
         ...item,
         closingStock: validatedValue,
         sales: Math.max(0, item.total - validatedValue),
         isClosingStockSet: true
       };
     }
     return item;
   });
   
   setFilteredAccessories(updateData(filteredAccessories));
   setAccessoryData(updateData(accessoryData));
 };

 const handleAccessoryClosingStockBlur = (id) => {
   setAccessoryEditingValues(prev => {
     const newValues = { ...prev };
     delete newValues[id];
     return newValues;
   });
 };

// Handler for accessory received stock changes
const handleAccessoryReceivedChange = (id, value) => {
  const currentItem = accessoryData.find(item => item.id === id);
  if (!currentItem) return;
  
  setAccessoryReceivedEditingValues(prev => ({ ...prev, [id]: value }));
  
  let numValue;
  if (value === '' || value === null || value === undefined) {
    numValue = 0;
  } else {
    numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) numValue = 0;
  }
  
  const newTotal = currentItem.openingStock + numValue;
  const newClosingStock = Math.min(currentItem.closingStock, newTotal);
  
  const updateData = (data) => data.map(item => {
    if (item.id === id) {
      return {
        ...item,
        received: numValue,
        total: newTotal,
        closingStock: newClosingStock,
        sales: Math.max(0, newTotal - newClosingStock)
      };
    }
    return item;
  });
  
  setFilteredAccessories(updateData(filteredAccessories));
  setAccessoryData(updateData(accessoryData));
};

const handleAccessoryReceivedBlur = (id) => {
  setAccessoryReceivedEditingValues(prev => {
    const newValues = { ...prev };
    delete newValues[id];
    return newValues;
  });
};

 const hasUnsavedAccessoryChanges = () => {
   return accessoryData.some(item => {
     const currentClosing = item.closingStock;
     const originalClosing = accessoryOriginalValues[item.id];
     const currentReceived = item.received;
     const originalReceived = accessoryOriginalReceivedValues[item.id];
     return currentClosing !== originalClosing || currentReceived !== originalReceived;
   });
 };

 const handleSaveAccessories = async () => {
   setAccessorySaving(true);
   try {
     const updates = accessoryData.map(item => ({
       accessory_id: item.id,
       closing_stock: item.closingStock,
       received_stock: item.received
     }));
     
     const response = await apiPost('/api/accessories/daily/closing-stock', {
       date: businessDate,
       updates
     });
     
     if (response.ok) {
       const result = await response.json();
       toast.success(`Accessory closing stock saved! ${result.updated + result.created} items processed`);
       setAccessoryEditingValues({});
       setAccessoryReceivedEditingValues({});
       fetchAccessoryStock();
     } else {
       const error = await response.json();
       toast.error(error.message || 'Failed to save accessory closing stock');
     }
   } catch (error) {
     console.error('Error saving accessory closing stock:', error);
     toast.error('Failed to save accessory closing stock');
   }
   setAccessorySaving(false);
 };

 const handleSaveWithRetry = async (attemptCount = 0) => {
   const maxRetries = 2;
   setSaving(true);
   setSaveProgress(0);
   setSaveStatus('Preparing to save...');
   setRetryCount(attemptCount);
   
  try {
    // Use business date instead of regular date
    const targetDate = businessDate;
    // Build payload: Save ALL items
    // - If closing stock was modified by user: save with modified value
    // - If closing stock was NOT modified: save with current value (which is total for unset items)
    const stockUpdates = stockData.map(item => {
      return {
        id: item.id,
        closingStock: item.closingStock
      };
    });
     
     setSaveStatus(`Saving ${stockUpdates.length} products...`);
     setSaveProgress(25);
     
     // Ensure session is valid before attempting CSRF/token and save
     const authed = await checkAuthStatus();
     if (!authed) {
       setSaving(false);
       setSaveStatus('');
       toast.error('Your session has expired. Please log in again to save closing stock.');
          if (logout) logout();
       return;
     }

     // Ensure CSRF token present before POST
     if (!getCSRFToken) {
       // no-op safeguard; function always exists
     }
     if (!getCSRFToken()) {
       const token = await getCSRFTokenFromServer();
       if (token) setCSRFToken(token);
     }

     let response = await apiPost('/api/closing-stock/update', {
       date: targetDate,
       stockUpdates: stockUpdates
     });

     setSaveProgress(75);
     setSaveStatus('Processing response...');

     if (response.ok) {
       const result = await response.json();
       setSaveProgress(100);
       setSaveStatus('Save completed successfully!');
       
       // Enhanced success message with batch processing details
       const successMessage = result.performance?.batchProcessing 
         ? `Closing stock updated successfully!\n\n📊 Batch Processing Results:\n• ${result.updatedCount} total records processed\n• ${result.existingUpdated || 0} existing records updated\n• ${result.newRecordsCreated || 0} new records created\n\n⚡ Performance: Optimized batch operation`
         : `Closing stock updated successfully!\n${result.updatedCount} products updated`;
       
       toast.success(successMessage, { duration: 5000 });
       setEditingValues({});
       setRetryCount(0);
       
       // Refresh the stock data to update the save status
       setTimeout(() => {
         setSaveStatus('');
         setSaveProgress(0);
         fetchTodayStock();
       }, 1500);
     } else {
       // If CSRF failure, try to refresh token once and retry
       if (response.status === 403) {
         try {
           const token = await getCSRFTokenFromServer();
           if (token) {
             setCSRFToken(token);
             response = await apiPost('/api/closing-stock/update', {
               date: targetDate,
               stockUpdates: stockUpdates
             });
             if (response.ok) {
               const result = await response.json();
               setSaveProgress(100);
               setSaveStatus('Save completed successfully!');
               const successMessage = result.performance?.batchProcessing 
                 ? `Closing stock updated successfully!\n\n📊 Batch Processing Results:\n• ${result.updatedCount} total records processed\n• ${result.existingUpdated || 0} existing records updated\n• ${result.newRecordsCreated || 0} new records created\n\n⚡ Performance: Optimized batch operation`
                 : `Closing stock updated successfully!\n${result.updatedCount} products updated`;
               toast.success(successMessage, { duration: 5000 });
               setEditingValues({});
               setRetryCount(0);
               setTimeout(() => {
                 setSaveStatus('');
                 setSaveProgress(0);
                 fetchTodayStock();
               }, 1500);
               setSaving(false);
               return;
             }
           }
         } catch (_) {
           // ignore and fall through to normal error path
         }
       }
       // Try to extract detailed error from backend
       let serverMessage = 'Server error occurred';
       try {
         const errorBody = await response.json();
         serverMessage = errorBody?.message || errorBody?.error || serverMessage;
       } catch (_) {
         // ignore JSON parse failure
       }
       throw new Error(serverMessage);
     }
   } catch (error) {
     console.error('Error saving closing stock:', error);
     setSaveProgress(0);
     
     // Enhanced error handling with retry logic
     if (attemptCount < maxRetries && (
       error.message.includes('timeout') || 
       error.message.includes('network') ||
       error.message.includes('connection')
     )) {
       setSaveStatus(`Save failed, retrying... (${attemptCount + 1}/${maxRetries + 1})`);
       console.log(`🔄 Retrying save attempt ${attemptCount + 1}/${maxRetries + 1}...`);
       
       // Wait before retry with exponential backoff
       const retryDelay = Math.min(2000 * Math.pow(2, attemptCount), 8000);
       setTimeout(() => {
         handleSaveWithRetry(attemptCount + 1);
       }, retryDelay);
       return;
     }
     
    // Final failure
    setSaveStatus('Save failed');
    setRetryCount(0);
    
    let errorMessage = 'Network error while saving';
    if (error.message.includes('timeout')) {
      errorMessage = '⏱️ Save operation timed out.\n\nThis might be due to a large number of products. The save might still be processing in the background.\n\nPlease wait a moment and refresh the page to check if your changes were saved.';
    } else if (error.message.includes('batch')) {
      errorMessage = `Batch processing error: ${error.message}\n\nPlease try again or contact support if the issue persists.`;
    } else {
      errorMessage = `Error: ${error.message}`;
    }
    
    toast.error(errorMessage, { duration: 6000 });
    
    setTimeout(() => {
      setSaveStatus('');
    }, 3000);
   }
   setSaving(false);
 };

 // Wrapper function for backward compatibility
 const handleSave = () => handleSaveWithRetry(0);

  if (loading) {
   return (
     <div className="flex justify-center items-center h-[50vh]">
       <div className="text-xl font-medium">Loading today's stock...</div>
     </div>
   );
 }

return (
      <main className="w-full px-4 py-8 space-y-6 max-w-7xl mx-auto">
      
      {/* Page Title Section */}
      <div className="space-y-2 text-left">
        <h1 className="text-4xl font-bold text-left">Update Closing Stock</h1>
        <p className="text-base text-muted-foreground text-left">
          Update end-of-day closing stock quantities
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="liquor" className="flex items-center gap-2">
            <Wine className="w-4 h-4" />
            Liquor ({filteredData.length})
          </TabsTrigger>
          <TabsTrigger value="accessories" className="flex items-center gap-2">
            <Coffee className="w-4 h-4" />
            Accessories ({filteredAccessories.length})
          </TabsTrigger>
        </TabsList>

        {/* LIQUOR TAB */}
        <TabsContent value="liquor" className="space-y-6 mt-6">
      {/* Controls Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        {/* Search Box */}
        <div className="relative w-full lg:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search by brand name or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Save Section */}
        <div className="flex flex-col items-end gap-2 w-full lg:w-auto">
          <Button 
            onClick={handleSave}
            disabled={saving}
            variant={hasUnsavedChanges() ? "destructive" : closingStockStatus?.isFullySaved ? "default" : "default"}
            className="w-full lg:w-auto"
          >
            {saving ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Saving...
              </>
            ) : hasUnsavedChanges() ? (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            ) : closingStockStatus?.isFullySaved ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Already Saved
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save All Changes
              </>
            )}
          </Button>
          
          {/* Progress indicator for save operations */}
          {saving && (
            <Card className="w-full lg:w-auto mt-2">
              <CardContent className="p-4">
                <Progress value={saveProgress} className="mb-2" />
                <div className="text-sm text-slate-600 text-center">
                  {saveStatus}
                  {retryCount > 0 && (
                    <span className="text-orange-600 font-semibold ml-1">
                      (Retry {retryCount}/3)
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Status Info */}
          {closingStockStatus && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">
                {closingStockStatus.savedProducts} of {closingStockStatus.totalProducts} products saved
              </Badge>
              {businessDate && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDateDash(businessDate)}
                </Badge>
              )}
              {hasUnsavedChanges() && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Unsaved changes
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Total Sale Value */}
      <div className="flex justify-end">
        <div className="bg-muted border rounded-lg px-6 py-3">
          <div className="text-sm text-muted-foreground">Total Sale Value</div>
          <div className="text-2xl font-bold text-foreground">
            ₹{filteredData.reduce((sum, item) => sum + (item.sales * item.pricePerUnit), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* Table Card */}
      <Card>
        <CardContent className="p-0">
          <div className="max-h-[70vh] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-muted z-10">
                <TableRow>
                  <TableHead className="font-semibold text-center">S.No</TableHead>
                  <TableHead className="font-semibold text-center">Brand Name</TableHead>
                  <TableHead className="font-semibold text-center">Size</TableHead>
                  <TableHead className="font-semibold text-center">O.S</TableHead>
                  <TableHead className="font-semibold text-center">Rec</TableHead>
                  <TableHead className="font-semibold text-center">Ttl</TableHead>
                  <TableHead className="font-semibold text-center">C.S</TableHead>
                  <TableHead className="font-semibold text-center">Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? (
                  getGroupedInventoryForDisplay().map((item) => (
                    <TableRow key={item.id}>
                      {item.isFirstVariant && (
                        <TableCell 
                          className="bg-muted/50 font-semibold text-center align-top border-r" 
                          rowSpan={item.variantCount}
                        >
                          {item.serialNumber}
                        </TableCell>
                      )}
                      {item.isFirstVariant && (
                        <TableCell 
                          className="bg-muted/50 font-medium align-top max-w-[200px] break-words border-r" 
                          rowSpan={item.variantCount}
                        >
                          {item.brandName}
                        </TableCell>
                      )}
                      <TableCell className="text-center align-middle">{item.displaySize}</TableCell>
                      <TableCell className="text-center align-middle">{item.openingStock}</TableCell>
                      <TableCell className="text-center align-middle">{item.received}</TableCell>
                      <TableCell className="text-center align-middle">{item.total}</TableCell>
                      <TableCell className="text-center align-middle">
                        <div className="flex items-center justify-center h-full">
                          <Input
                            type="number"
                            className={`w-16 text-center px-1 ${
                              !item.isClosingStockSet 
                                ? 'bg-yellow-50 border-yellow-400 text-yellow-800' 
                                : ''
                            }`}
                                style={{ textAlign: 'center', MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' }}
                            value={editingValues.hasOwnProperty(item.id) ? editingValues[item.id] : item.closingStock}
                            onChange={(e) => handleClosingStockChange(item.id, e.target.value)}
                            onBlur={() => handleClosingStockBlur(item.id)}
                            onWheel={(e) => e.target.blur()}
                            onFocus={(e) => { e.target.select(); handleClosingStockFocus(item.id); }}
                            min="0"
                            max={item.total}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center align-middle font-semibold">
                        {typeof item.sales === 'number' ? item.sales : 0}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground italic">
                      {searchTerm ? 'No products found matching your search' : 'No stock data available'}
                    </TableCell>
                  </TableRow>
                )}
                {filteredData.length > 0 && (
                  <TableRow className="bg-muted font-bold border-t-2">
                        <TableCell colSpan={3} className="text-center font-bold">TOTAL</TableCell>
                        <TableCell className="text-center font-bold">{filteredData.reduce((sum, item) => sum + item.openingStock, 0)}</TableCell>
                        <TableCell className="text-center font-bold">{filteredData.reduce((sum, item) => sum + item.received, 0)}</TableCell>
                        <TableCell className="text-center font-bold">{filteredData.reduce((sum, item) => sum + item.total, 0)}</TableCell>
                        <TableCell className="text-center font-bold">{filteredData.reduce((sum, item) => sum + item.closingStock, 0)}</TableCell>
                        <TableCell className="text-center font-bold">{filteredData.reduce((sum, item) => sum + item.sales, 0)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* ACCESSORIES TAB */}
        <TabsContent value="accessories" className="space-y-6 mt-6">
          {/* Controls Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search accessories..."
                value={accessorySearchTerm}
                onChange={(e) => setAccessorySearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex flex-col items-end gap-2 w-full lg:w-auto">
              <Button 
                onClick={handleSaveAccessories}
                disabled={accessorySaving || accessoryData.length === 0}
                variant={hasUnsavedAccessoryChanges() ? "destructive" : accessoryStatus?.isFullySaved ? "default" : "default"}
                className="w-full lg:w-auto"
              >
                {accessorySaving ? (
                  <><span className="animate-spin mr-2">⏳</span>Saving...</>
                ) : hasUnsavedAccessoryChanges() ? (
                  <><Save className="w-4 h-4 mr-2" />Save Changes</>
                ) : accessoryStatus?.isFullySaved ? (
                  <><CheckCircle className="w-4 h-4 mr-2" />Already Saved</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save All Changes</>
                )}
              </Button>
              
              {accessoryStatus && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="secondary">
                    {accessoryStatus.savedProducts} of {accessoryStatus.totalProducts} items saved
                  </Badge>
                  {businessDate && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDateDash(businessDate)}
                    </Badge>
                  )}
                  {hasUnsavedAccessoryChanges() && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Unsaved changes
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Total Sale Value */}
          <div className="flex justify-end">
            <div className="bg-muted border rounded-lg px-6 py-3">
              <div className="text-sm text-muted-foreground">Total Accessory Sale Value</div>
              <div className="text-2xl font-bold text-foreground">
                ₹{filteredAccessories.reduce((sum, item) => sum + (item.sales * item.pricePerUnit), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Accessories Table */}
          <Card>
            <CardContent className="p-0">
              {accessoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="max-h-[70vh] overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-muted z-10">
                      <TableRow>
                        <TableHead className="font-semibold text-center">S.No</TableHead>
                        <TableHead className="font-semibold text-center">Name</TableHead>
                        <TableHead className="font-semibold text-center">Size</TableHead>
                        <TableHead className="font-semibold text-center">O.S</TableHead>
                        <TableHead className="font-semibold text-center">Rec</TableHead>
                        <TableHead className="font-semibold text-center">Ttl</TableHead>
                        <TableHead className="font-semibold text-center">C.S</TableHead>
                        <TableHead className="font-semibold text-center">Sales</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccessories.length > 0 ? (
                        filteredAccessories.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell className="text-center font-medium">{index + 1}</TableCell>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell className="text-center">{item.size}</TableCell>
                            <TableCell className="text-center">{item.openingStock}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center">
                                <Input
                                  type="number"
                                  className="w-16 text-center px-1"
                                  style={{ textAlign: 'center', MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' }}
                                  value={accessoryReceivedEditingValues.hasOwnProperty(item.id) ? accessoryReceivedEditingValues[item.id] : item.received}
                                  onChange={(e) => handleAccessoryReceivedChange(item.id, e.target.value)}
                                  onBlur={() => handleAccessoryReceivedBlur(item.id)}
                                  onWheel={(e) => e.target.blur()}
                                  onFocus={(e) => e.target.select()}
                                  min="0"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{item.total}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center">
                                <Input
                                  type="number"
                                  className={`w-16 text-center px-1 ${!item.isClosingStockSet ? 'bg-yellow-50 border-yellow-400 text-yellow-800' : ''}`}
                                  style={{ textAlign: 'center', MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' }}
                                  value={accessoryEditingValues.hasOwnProperty(item.id) ? accessoryEditingValues[item.id] : item.closingStock}
                                  onChange={(e) => handleAccessoryClosingStockChange(item.id, e.target.value)}
                                  onBlur={() => handleAccessoryClosingStockBlur(item.id)}
                                  onWheel={(e) => e.target.blur()}
                                  onFocus={(e) => e.target.select()}
                                  min="0"
                                  max={item.total}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-semibold">{item.sales}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground italic">
                            {accessorySearchTerm ? 'No accessories found matching your search' : 'No accessories added yet. Go to Manage Stock → Accessories to add items.'}
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredAccessories.length > 0 && (
                        <TableRow className="bg-muted font-bold border-t-2">
                          <TableCell colSpan={3} className="text-center font-bold">TOTAL</TableCell>
                          <TableCell className="text-center font-bold">{filteredAccessories.reduce((sum, item) => sum + item.openingStock, 0)}</TableCell>
                          <TableCell className="text-center font-bold">{filteredAccessories.reduce((sum, item) => sum + item.received, 0)}</TableCell>
                          <TableCell className="text-center font-bold">{filteredAccessories.reduce((sum, item) => sum + item.total, 0)}</TableCell>
                          <TableCell className="text-center font-bold">{filteredAccessories.reduce((sum, item) => sum + item.closingStock, 0)}</TableCell>
                          <TableCell className="text-center font-bold">{filteredAccessories.reduce((sum, item) => sum + item.sales, 0)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}

export default UpdateClosingStock;
